<template lang="pug">

    //- Der Trigger
    div.item.user-menu-trigger(ref="menuTrigger" @click.stop="togglePopup")
        .user-avatar
            .avatar-wrapper
                span.avatar-initial {{ initial }}
                img.avatar-image(v-if="avatarUrl" :src="avatarUrl")
        .trigger-user-info(v-if="isExpanded")
            div.trigger-fullname {{ fullName || $auth.username }}
            div.trigger-role {{ activeRoleLabel }}

        //- Vertikales Menü – wird in <body> verschoben und per fixed positioniert
        div.ui.vertical.menu.user-menu-popup(ref="menuPopup" :style="popupStyle")

            //- Benutzerinformation mit Aktions-Icons oben rechts
            div.user-info-header
                div.user-info-content
                    div.user-fullname {{ fullName || $auth.username }}
                    div.user-role {{ activeRoleLabel }}
                div.user-info-actions
                    router-link.action-icon(
                        v-if="canEditProfile"
                        :to="{ name: 'users/update', params: { id: $auth.userId } }"
                        title="Profil bearbeiten"
                        @click.stop="closePopup"
                    )
                        i.user.edit.icon
                    button.action-icon(@click.stop="toggleMode" :title="isDarkMode ? 'Light Mode' : 'Dark Mode'")
                        i(:class="isDarkMode ? 'sun icon' : 'moon icon'")

            //- Rollenswitch
            template(v-if="$auth.roleIds.length > 1")
                div.header Rolle wechseln

                a.item(
                    v-if='canBeCustomer && activeRole !== "CUSTOMER"'
                    v-on:click='changeRole(customerRole)'
                )
                    i.exchange.alternate.icon
                    |  Kunde

                a.item(
                    v-if='canBeAgency && activeRole !== "AGENCY"'
                    v-on:click='changeRole(agencyRole)'
                )
                    i.exchange.alternate.icon
                    |  Agentur

                a.item(
                    v-if='canBeSupplier && activeRole !== "SUPPLIER"'
                    v-on:click='changeRole(supplierRole)'
                )
                    i.exchange.alternate.icon
                    |  Lieferant

                a.item(
                    v-if='canBeMediaProduction && activeRole !== "MEDIA_PRODUCTION"'
                    v-on:click='changeRole("MEDIA_PRODUCTION")'
                )
                    i.exchange.alternate.icon
                    |  Medienproduktion

                a.item(
                    v-if='canBeAdmin && activeRole !== "ADMIN"'
                    v-on:click='changeRole("ADMIN")'
                )
                    i.exchange.alternate.icon
                    |  Administration

            //- Akzentfarbe
            div.header Akzentfarbe

            div.item.color-item
                div.color-grid
                    div.color-dot(
                        v-for="color in availablePrimaryColors"
                        :key="color.value"
                        :title="color.name"
                        :class="{ active: currentPrimaryColor === color.value }"
                        :style="{ backgroundColor: color.value }"
                        @click.stop="setPrimaryColor(color.value)"
                    )

            div.divider

            //- Logout
            a.item.logout-item(v-on:click='logout()')
                i.sign.out.icon
                |  Logout

</template>

<script>
    import md5 from 'md5'
    import { default_route } from '@/utils/role'

    //noinspection JSUnusedGlobalSymbols
    export default {

        data() {
            return {
                isOpen: false,
                popupStyle: { display: 'none' }
            }
        },

        props: {
            isExpanded: {
                type: Boolean,
                default: false
            }
        },

        computed: {

            activeRole() {
                return this.$auth.activeRole
            },

            activeRoleLabel() {
                const labels = {
                    CUSTOMER: 'Kunde',
                    AGENCY: 'Agentur',
                    SUPPLIER: 'Lieferant',
                    MEDIA_PRODUCTION: 'Medienproduktion',
                    ADMIN: 'Administrator',
                }
                return labels[this.activeRole] || this.activeRole
            },

            agencyRole() {
                return this.$auth.roleIds.find(role => role.includes('AGENCY'))
            },

            canBeAdmin() {
                return this.$auth.roleIds.includes('ADMIN')
            },

            canBeAgency() {
                return this.$auth.roleIds.includes('AGENCY')
            },

            canBeCustomer() {
                return this.$auth.roleIds.includes('CUSTOMER')
            },

            canBeMediaProduction() {
                return this.$auth.roleIds.includes('MEDIA_PRODUCTION')
            },

            canBeSupplier() {
                return this.$auth.roleIds.includes('SUPPLIER')
            },

            customerRole() {
                return this.$auth.roleIds.find(role => role.includes('CUSTOMER'))
            },

            supplierRole() {
                return this.$auth.roleIds.find(role => role.includes('SUPPLIER'))
            },

            person() {
                return this.$store.getters['persons/byId'](this.$auth.personId)
            },

            email() {
                return this.person ? (this.person.email || '').toLowerCase().trim() : ''
            },

            firstName() {
                return this.person ? (this.person.firstName || '') : ''
            },

            lastName() {
                return this.person ? (this.person.lastName || '') : ''
            },

            fullName() {
                return [this.firstName, this.lastName].filter(Boolean).join(' ')
            },

            initial() {
                const first = this.firstName ? this.firstName.charAt(0).toUpperCase() : ''
                const last  = this.lastName  ? this.lastName.charAt(0).toUpperCase()  : ''
                if (first || last) return first + last
                return (this.$auth.username || '?').charAt(0).toUpperCase()
            },

            canEditProfile() {
                return ['ADMIN', 'MEDIA_PRODUCTION'].includes(this.activeRole)
            },

            // Preferred avatar: MOCO image (avatar_url) if present, otherwise Gravatar fallback.
            avatarUrl() {
                if (this.person && this.person.avatar_url) return this.person.avatar_url
                return this.gravatarUrl
            },

            gravatarUrl() {
                if (!this.email) return ''
                return `https://www.gravatar.com/avatar/${md5(this.email)}?s=40&d=blank`
            },

            isDarkMode() {
                return this.$store.state.theme.mode === 'dark'
            },

            currentPrimaryColor() {
                return this.$store.state.theme.primaryColor
            },

            availablePrimaryColors() {
                return this.$store.state.theme.availablePrimaryColors
            },

        },

        methods: {

            toggleMode() {
                this.$store.dispatch('theme/toggleMode')
            },

            setPrimaryColor(color) {
                this.$store.dispatch('theme/setPrimaryColor', color)
            },

            togglePopup() {
                if (this.isOpen) {
                    this.closePopup()
                } else {
                    this.openPopup()
                }
            },

            openPopup() {
                this.isOpen = true
                this.$nextTick(() => {
                    const triggerRect = this.$refs.menuTrigger.getBoundingClientRect()
                    this.popupStyle = {
                        display:   'block',
                        position:  'fixed',
                        bottom:    '6px',
                        left:      (triggerRect.right + 8 - (this.isExpanded ? 100 : 0)) + 'px',
                        maxHeight: 'calc(100vh - 32px)',
                        overflowY: 'auto',
                    }
                    document.addEventListener('click', this.onClickOutside)
                })
            },

            closePopup() {
                this.isOpen = false
                this.popupStyle = { display: 'none' }
                document.removeEventListener('click', this.onClickOutside)
            },

            onClickOutside(e) {
                if (
                    this.$refs.menuTrigger && !this.$refs.menuTrigger.contains(e.target) &&
                    this.$refs.menuPopup   && !this.$refs.menuPopup.contains(e.target)
                ) {
                    this.closePopup()
                }
            },

            async changeRole(role) {
                const token = await this.$auth.changeRole(role)
                const route = default_route(token.activeRoleId)
                const { href } = this.$router.resolve(route)
                window.location.assign(href)
            },

            async logout() {
                await this.$auth.logout()
                window.location.assign('/login')
            }

        },

        created() {
            this.$store.dispatch('persons/loadOneByUserId', { userId: this.$auth.userId })
        },

        mounted() {
            // Popup aus dem Sidebar-DOM herausziehen – Semantic UI nutzt transform: translate3d(),
            // was einen neuen Containing Block schafft und position:fixed relativ zur Sidebar macht.
            // Nach body.appendChild wird es korrekt relativ zum Viewport positioniert.
            document.body.appendChild(this.$refs.menuPopup)
        },

        beforeDestroy() {
            document.removeEventListener('click', this.onClickOutside)
            if (this.$refs.menuPopup && this.$refs.menuPopup.parentNode) {
                this.$refs.menuPopup.parentNode.removeChild(this.$refs.menuPopup)
            }
        }

    }
</script>

<style>
    /* Nicht scoped, da das Menü nach <body> verschoben wird */

    /* ── Trigger ─────────────────────────────────────────── */
    .user-menu-trigger {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        text-align: left !important;
    }

    .expanded .user-menu-trigger {
        justify-content: flex-start !important;
    }

    .user-menu-trigger .user-avatar {
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
        outline: 2px solid var(--primaryColor, transparent);
        outline-offset: 0px;
        border-radius: 100%;
    }

    .user-menu-trigger .trigger-user-info {
        margin-left: 10px;
        overflow: hidden;
        min-width: 0;
    }

    .user-menu-trigger .trigger-fullname {
        font-size: 0.875rem;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.3;
    }

    .user-menu-trigger .trigger-role {
        font-size: 0.75rem;
        opacity: 0.65;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.3;
        margin-top: 0.1rem;
    }

    .user-menu-trigger .avatar-wrapper {
        position: relative;
        width: 2rem;
        height: 2rem;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
    }

    .user-menu-trigger .avatar-image {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .user-menu-trigger .avatar-initial {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #4b5563;
        color: #ffffff;
        font-size: 0.75rem;
        font-weight: 600;
        user-select: none;
    }

    /* ── Menü-Panel ──────────────────────────────────────── */
    .user-menu-popup.ui.vertical.menu {
        z-index: 9999 !important;
        min-width: 230px;
        border-radius: 0.375rem !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2) !important;
        overflow: hidden;
    }

    /* ── Benutzerinfo (oben, nicht anklickbar) ───────────── */
    .user-menu-popup .user-info-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1rem 0.65rem;
        cursor: default;
        border-bottom: 1px solid rgba(0,0,0,0.08);
    }

    .user-menu-popup .user-info-content {
        pointer-events: none;
    }

    .user-menu-popup .user-info-actions {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        margin-left: 0.75rem;
        flex-shrink: 0;
    }

    .user-menu-popup .action-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.75rem;
        height: 1.75rem;
        border-radius: 0.25rem;
        background: none;
        border: none;
        cursor: pointer;
        color: var(--textMutedColor, #6b7280);
        transition: background 0.15s ease, color 0.15s ease;
        padding: 0;
        text-decoration: none;
    }

    .user-menu-popup .action-icon:hover {
        background: rgba(0, 0, 0, 0.06);
        color: var(--textColor, #111827);
    }

    .user-menu-popup .action-icon i.icon {
        margin: 0 !important;
        font-size: 0.875rem;
    }

    .user-menu-popup .user-fullname {
        font-weight: 600;
        font-size: 0.875rem;
        color: var(--textColor, #111827);
        line-height: 1.3;
    }

    .user-menu-popup .user-role {
        font-size: 0.75rem;
        color: var(--textMutedColor, #6b7280);
        margin-top: 0.15rem;
        line-height: 1.3;
    }

    /* ── Farbauswahl ─────────────────────────────────────── */
    .user-menu-popup .color-item {
        display: block !important;
    }

    .user-menu-popup .color-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .user-menu-popup .color-dot {
        width: 1.25rem;
        height: 1.25rem;
        border-radius: 50%;
        cursor: pointer;
        transition: transform 0.15s ease;
        border: 2px solid transparent;
    }

    .user-menu-popup .color-dot:hover {
        transform: scale(1.2);
    }

    .user-menu-popup .color-dot.active {
        border-color: #fff;
        box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35);
    }

    /* ── Logout ──────────────────────────────────────────── */
    .user-menu-popup .logout-item {
        color: #ef4444 !important;
    }

    .user-menu-popup .logout-item:hover {
        background: rgba(239, 68, 68, 0.08) !important;
    }

    /* ── Allgemeine Item-Ausrichtung ─────────────────────── */
    .user-menu-popup .header {
        padding-left: 15px !important;
        font-weight: 600 !important;
        padding-bottom: 4px;
        padding-top: 15px;
    }

    .user-menu-popup .item {
        display: flex !important;
        align-items: center !important;
    }

    .user-menu-popup .item > i.icon {
        margin: 0 0.75rem 0 0 !important;
    }
</style>
